import { useEffect } from "react";
import { InitiateAmplitudeUser, trackEvent } from "src/Amplitude";
import { UserRes } from "src/store/features/api/generated";
import { setSelectedCurrency } from "src/store/features/settings.slice";
import {
  setBroker,
  setDefaultTradeFee,
  setIs2FAEnabled,
  setIsCustomerTalkingWithBroker,
  setKycSteps,
  setReferralSignUpCode,
  setUserData,
  setUserDefaultCurrency,
  setIkycDetails,
  setUserHoldings,
  setUserOverview,
  setIsUserVerified,
  setSalesforceDetails,
  setAccountCreateTime,
  setUserAddress,
  setKycStatus,
  setIsPendingVerification,
  setUserStateDisclosures,
  setUserDepositDisclosures,
  type DepositDisclosureData,
} from "src/store/features/user.slice";
import { useAppDispatch } from "src/store/store";
import { getSessionItem, setSessionCookie } from "../SessionManagement";

export default function useHydrateUser({
  userData,
  checkFirstTimeLogin,
}: {
  userData: UserRes | undefined;
  checkFirstTimeLogin: boolean;
}) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (userData) {
      const {
        broker,
        details,
        loginCount,
        referralSignUpCode,
        defaultCurrencyConfig,
        kycSteps,
        is2FAEnabled,
        isCustomerTalkingWithBroker,
        rekycDetails,
        kycStatus,
        overview,
        defaultTradeFee,
        holdings,
        isUnverified,
        isPendingVerification,
        salesforceDetails,
        accountCreateTime,
        address,
        stateDisclosures,
        depositDisclosures,
      } = userData;

      // Set Account Create Time, used to calculate if < then 8 months to determine if legacy client
      dispatch(setAccountCreateTime(accountCreateTime));

      // Set customer address
      dispatch(setUserAddress(address));

      // Set user state disclosures
      if (stateDisclosures && Array.isArray(stateDisclosures)) {
        dispatch(setUserStateDisclosures(stateDisclosures));
      }

      // Set user deposit disclosures (API returns type: string; slice expects "Crypto" | "Fiat")
      if (depositDisclosures && Array.isArray(depositDisclosures)) {
        dispatch(
          setUserDepositDisclosures(depositDisclosures as DepositDisclosureData[]),
        );
      }

      // Set Default Trade Fee
      dispatch(setDefaultTradeFee(defaultTradeFee));

      // Set user details
      dispatch(setUserData({ ...details, loginCount }));

      // Set user settings
      dispatch(setIs2FAEnabled(is2FAEnabled));

      // Set user Broker
      if (broker) dispatch(setBroker(broker));

      // Set user referrao signup code
      if (referralSignUpCode) dispatch(setReferralSignUpCode(referralSignUpCode));

      // Set user KYC steps
      dispatch(setKycSteps(kycSteps));

      // set user rekyc details
      if (rekycDetails) dispatch(setIkycDetails(rekycDetails));

      dispatch(setKycStatus(kycStatus ?? ""));

      // set user salesforce details
      if (salesforceDetails) dispatch(setSalesforceDetails(salesforceDetails));

      // v1: selected currency
      dispatch(setSelectedCurrency(defaultCurrencyConfig.code));

      // v2: Set user default currency
      dispatch(setUserDefaultCurrency(defaultCurrencyConfig.code));

      // Initiate Amplitude user
      InitiateAmplitudeUser(details);

      // Open or keep closed AreYouTalkingWithABrokerDialog component
      dispatch(setIsCustomerTalkingWithBroker(isCustomerTalkingWithBroker));

      // v2: set the users overview
      dispatch(setUserOverview(overview));

      // set users holdings
      dispatch(setUserHoldings(holdings));
      // set users holdings
      dispatch(setIsUserVerified(isUnverified));

      dispatch(setIsPendingVerification(isPendingVerification));

      if (checkFirstTimeLogin) {
        // Check if first login has already been logged
        const sessionLogged = getSessionItem("firstLoginlogged");

        if (loginCount === 1 && !sessionLogged) {
          trackEvent("First time login", { email: details.email }, "Onboarding");
        }

        // Set session cookie
        setSessionCookie("firstLoginlogged", "true");
      }
    }
  }, [userData]); // runs when userData changes

  return;
}
